/**
 * "Latest results" — the newest finished runs, deliverable-first: outcome
 * glyph, what was asked, when + how fast, and the file count. Rows open the
 * read-only conversation view.
 */
import { Badge, Icon } from '@evt/flow-stream-ui'
import type { RunStatus, RunSummary } from '@portal/contracts'
import { Link, useNavigate } from 'react-router-dom'
import type { Messages } from '../../i18n'
import { useI18n } from '../../i18n'
import { fmtDurationClock } from './home-format'

export function LatestResults({ recent, flowNames }: { recent: RunSummary[]; flowNames: Map<string, string> }) {
  const { t } = useI18n()
  const m = t.home
  const navigate = useNavigate()

  return (
    <section>
      <div className="home-section-head">
        <h2 className="home-section-title">{m.latestResults}</h2>
        <Link className="home-more" to="/conversations">
          {m.history} →
        </Link>
      </div>
      {recent.length === 0 ? (
        <div className="home-empty card">
          <Icon name="file" size={16} />
          <span>{m.firstDeliverable}</span>
        </div>
      ) : (
        <div className="latest-list">
          {recent.map((run) => (
            <ResultRow
              key={run.executionId}
              run={run}
              displayName={flowNames.get(run.flowId) ?? run.displayName}
              onOpen={() => navigate(`/conversations/${encodeURIComponent(run.executionId)}`)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

interface Outcome {
  glyph: 'check' | 'x'
  className: string
  badge: 'success' | 'partial' | 'failure'
  label: string
}

function outcomeFor(status: RunStatus, m: Messages['home']): Outcome {
  switch (status) {
    case 'success':
      return { glyph: 'check', className: 'is-success', badge: 'success', label: m.outcomeDone }
    case 'partial_success':
      return { glyph: 'check', className: 'is-partial', badge: 'partial', label: m.outcomePartial }
    default:
      return { glyph: 'x', className: 'is-failure', badge: 'failure', label: m.outcomeFailed }
  }
}

function ResultRow({ run, displayName, onOpen }: { run: RunSummary; displayName: string; onOpen: () => void }) {
  const { t, formatAgo } = useI18n()
  const m = t.home
  const outcome = outcomeFor(run.status, m)
  const when = formatAgo(run.finishedAt ?? run.startedAt)
  const took = fmtDurationClock(run.durationMs)

  return (
    <div className="latest-row card" onClick={onOpen}>
      <div className={`latest-glyph ${outcome.className}`}>
        <Icon name={outcome.glyph} size={16} />
      </div>
      <div className="latest-main">
        <div className="latest-title">{displayName}</div>
        {(run.briefPreview || run.startedBy) && (
          <div className="latest-brief muted">{run.briefPreview ?? m.startedByName(run.startedBy ?? '')}</div>
        )}
      </div>
      <div className="latest-right">
        <span className="latest-when muted">
          {when}
          {took !== '—' ? ` · ${took}` : ''}
        </span>
        {run.filesOut != null && run.filesOut > 0 && (
          <span className="latest-files chip">
            <Icon name="download" size={11} /> {m.filesCount(run.filesOut)}
          </span>
        )}
        <Badge kind={outcome.badge} dot>
          {outcome.label}
        </Badge>
        <Icon name="chevron" size={14} className="latest-chevron" />
      </div>
    </div>
  )
}
